import { cn } from '@/lib/cn';
import type { DataSourceIndex } from '@innermate/shared';
import { KeyRound, ShieldCheck } from 'lucide-react';

interface IndexTableProps {
  indexes: DataSourceIndex[];
}

export function IndexTable({ indexes }: IndexTableProps) {
  if (indexes.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-surface/50 px-6 py-8 text-center text-sm text-foreground-muted">
        이 테이블에는 인덱스가 없습니다.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-border bg-surface overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-muted/40 text-[11px] uppercase tracking-wider text-foreground-muted">
          <tr>
            <Th>인덱스</Th>
            <Th>컬럼</Th>
            <Th>종류</Th>
            <Th>WHERE</Th>
          </tr>
        </thead>
        <tbody>
          {indexes.map((idx) => (
            <IndexRow key={idx.id} idx={idx} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2.5 font-medium text-left">{children}</th>;
}

function IndexRow({ idx }: { idx: DataSourceIndex }) {
  const isRemoved = !!idx.removedAt;
  return (
    <tr className={cn('border-t border-border align-top', isRemoved && 'opacity-50')}>
      <td className="px-3 py-3">
        <div className="flex items-center gap-1.5">
          {idx.isPrimary ? (
            <KeyRound className="size-3.5 text-accent" aria-label="PRIMARY" />
          ) : idx.isUnique ? (
            <ShieldCheck className="size-3.5 text-foreground-muted" aria-label="UNIQUE" />
          ) : null}
          <span className="font-medium font-mono text-xs">{idx.indexName}</span>
        </div>
      </td>
      <td className="px-3 py-3 font-mono text-xs text-foreground-muted">
        {idx.columnNames.length > 0 ? idx.columnNames.join(', ') : '—'}
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap gap-1">
          {idx.isPrimary ? <Badge tone="accent">PRIMARY</Badge> : null}
          {idx.isUnique && !idx.isPrimary ? <Badge>UNIQUE</Badge> : null}
          {idx.indexMethod ? <Badge tone="muted">{idx.indexMethod}</Badge> : null}
        </div>
      </td>
      <td className="px-3 py-3 text-xs text-foreground-muted font-mono">{idx.predicate ?? '—'}</td>
    </tr>
  );
}

function Badge({
  children,
  tone = 'default',
}: {
  children: React.ReactNode;
  tone?: 'default' | 'accent' | 'muted';
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm px-1.5 h-5 text-[10px] uppercase tracking-wider',
        tone === 'accent' && 'bg-accent/15 text-accent',
        tone === 'muted' && 'bg-surface-muted/60 text-foreground-muted',
        tone === 'default' && 'border border-border text-foreground-muted',
      )}
    >
      {children}
    </span>
  );
}
